<script setup>
import {onMounted, ref} from 'vue';
import {useRouter} from 'vue-router';
import ServiceRequirementService from '@/services/ServiceRequirementService.js';
import {formatDate, showSuccessMessage, getCssVar, getContrastColor} from '@/helper';
import {getLoggedUser} from '@/services/AuthService';
import {useI18n} from 'vue-i18n';
import Button from '@/components/forms/Button.vue';

const {t} = useI18n();
const router = useRouter();
const serviceRequirementId = router.currentRoute.value.params.id;

const serviceRequirement = ref(null);
const comments = ref([]);
const isLoading = ref(true);
const isSending = ref(false);
// IMA-511: klient ve stavu Inheritance_process/Inheritance_closed/Invalid má jen náhled -
// přidání komentáře by stejně zamítl backend (403) beze změny, tady schováváme formulář předem.
const isReadOnly = ref(false);
getLoggedUser().then(user => {
    isReadOnly.value = !!user?.client?.is_read_only;
});

// Formulář pro nový komentář
const newCommentText = ref('');
const newCommentFiles = ref([]);
const commentFileInput = ref(null);

onMounted(() => {
    fetchDetail();
});

function fetchDetail() {
    isLoading.value = true;
    ServiceRequirementService.getServiceRequirement(serviceRequirementId).then(data => {
        serviceRequirement.value = data;
        // EXTERNAL komentáře jsou již filtrovány backendem pro roli CLIENT
        comments.value = data.comments ?? [];
    }).finally(() => {
        isLoading.value = false;
    });
}

function handleFileChange(e) {
    newCommentFiles.value = Array.from(e.target.files);
}

function sendComment() {
    if (!newCommentText.value.trim()) return;
    isSending.value = true;

    const formData = new FormData();
    formData.append('text', newCommentText.value);
    // Klient může posílat pouze EXTERNAL komentáře
    formData.append('type', 'EXTERNAL');
    newCommentFiles.value.forEach(file => formData.append('attachments[]', file));

    ServiceRequirementService.addComment(serviceRequirementId, formData).then(comment => {
        comments.value.push(comment);
        newCommentText.value = '';
        newCommentFiles.value = [];
        if (commentFileInput.value) commentFileInput.value.value = '';
        showSuccessMessage(t('clientServiceRequirementDetail.comment_sent'));
    }).finally(() => {
        isSending.value = false;
    });
}

/** Vrací inline styl pro badge statusu — barva textu je vypočtena dle WCAG kontrastu. */
function statusBadgeStyle(status) {
    const bg1 = getCssVar('--primaryColor', '#004148');
    const bg2 = getCssVar('--primaryHoverColor', '#046e7a');
    const primary = { color: getContrastColor(bg1), backgroundColor: bg1, borderColor: bg1 };
    const hover   = { color: getContrastColor(bg2), backgroundColor: bg2, borderColor: bg2 };
    const light   = { color: bg1, backgroundColor: '#f0f2f4', borderColor: '#f0f2f4' };
    const map = {
        NEW: light,
        IN_PROGRESS: primary,
        WAITING_FOR_CLIENT: hover,
        WAITING_FOR_BO: light,
        CLOSED: primary,
    };
    return map[status] ?? light;
}
</script>

<template>
    <div class="page-section">
        <div class="page-section__heading">
            <div class="row">
                <div class="col-3">
                    <a @click="router.back()" class="page-section__heading__back">
                        <i class="mdi mdi-arrow-left"></i> {{ t('clientServiceRequirementDetail.back') }}
                    </a>
                </div>
                <div class="col-6 text-center">
                    <div class="page-section__title">{{ t('clientServiceRequirementDetail.title') }}</div>
                </div>
            </div>
        </div>

        <div class="page-section__content">
            <div v-if="isLoading" class="text-center py-5">
                <span class="mdi mdi-loading mdi-spin me-2"></span>
            </div>

            <template v-else-if="serviceRequirement">
                <div class="row mb-4">
                    <!-- Základní info -->
                    <div class="col-12 col-md-6">
                        <div class="card">
                            <div class="card-header">
                                <div class="card-header__title">{{ t('clientServiceRequirementDetail.basic_info') }}</div>
                            </div>
                            <div class="card-body">
                                <div class="info-table">
                                    <div class="info-table__row row">
                                        <span class="info-table__label col-5">{{ t('clientServiceRequirementDetail.type') }}:</span>
                                        <span class="info-table__value col-7">{{ t('serviceRequirements.' + serviceRequirement.type, serviceRequirement.type) }}</span>
                                    </div>
                                    <div class="info-table__row row">
                                        <span class="info-table__label col-5">{{ t('clientServiceRequirementDetail.created_at') }}:</span>
                                        <span class="info-table__value col-7">{{ formatDate(serviceRequirement.created_at, 'd.m.Y H:i') }}</span>
                                    </div>
                                    <div class="info-table__row row">
                                        <span class="info-table__label col-5">{{ t('clientServiceRequirementDetail.author') }}:</span>
                                        <span class="info-table__value col-7">{{ serviceRequirement.user?.label }}</span>
                                    </div>
                                    <div class="info-table__row row">
                                        <span class="info-table__label col-5">{{ t('clientServiceRequirementDetail.status') }}:</span>
                                        <span class="info-table__value col-7">
                                            <span class="badge" :style="statusBadgeStyle(serviceRequirement.status)">
                                                {{ t('clientServiceRequirements.status_' + serviceRequirement.status, serviceRequirement.status) }}
                                            </span>
                                        </span>
                                    </div>
                                    <div class="info-table__row row" v-if="serviceRequirement.is_resolved">
                                        <span class="info-table__label col-5">{{ t('clientServiceRequirementDetail.resolved_at') }}:</span>
                                        <span class="info-table__value col-7">{{ formatDate(serviceRequirement.resolved_at, 'd.m.Y H:i') }}</span>
                                    </div>
                                    <div class="info-table__row row">
                                        <span class="info-table__label col-5">{{ t('clientServiceRequirementDetail.message') }}:</span>
                                        <span class="info-table__value col-7">{{ serviceRequirement.text }}</span>
                                    </div>
                                    <!-- Přílohy požadavku -->
                                    <div class="info-table__row row" v-if="serviceRequirement.attachments?.length">
                                        <span class="info-table__label col-5">{{ t('clientServiceRequirementDetail.attachments') }}:</span>
                                        <span class="info-table__value col-7">
                                            <div class="d-flex flex-wrap gap-2 mt-1">
                                                <router-link
                                                    v-for="att in serviceRequirement.attachments"
                                                    :key="att.uuid"
                                                    :to="{ name: 'FileDetail', params: { id: att.id } }"
                                                    class="d-inline-flex align-items-center gap-1 text-decoration-none small text-body border rounded px-2 py-1"
                                                >
                                                    <i class="mdi mdi-paperclip"></i>
                                                    {{ att.file_name }}
                                                </router-link>
                                            </div>
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Stav vyřešení — zobrazí se pouze pokud je požadavek vyřešen -->
                    <div v-if="serviceRequirement.is_resolved" class="col-12 col-md-6 d-flex align-items-start">
                        <div class="alert alert-success mt-3 mt-md-0">
                            <i class="mdi mdi-check-circle me-1"></i>
                            {{ t('clientServiceRequirementDetail.already_resolved') }}
                        </div>
                    </div>
                </div>

                <!-- Historie komunikace -->
                <div class="card mb-4">
                    <div class="card-header">
                        <div class="card-header__title">{{ t('clientServiceRequirementDetail.communication') }}</div>
                    </div>
                    <div class="card-body">
                        <div v-if="!comments.length" class="text-muted text-center py-3">
                            {{ t('clientServiceRequirementDetail.no_comments') }}
                        </div>
                        <div v-else class="d-flex flex-column gap-3">
                            <div
                                v-for="comment in comments"
                                :key="comment.id"
                                class="p-3 rounded bg-light border-start border-3"
                                :style="{ '--bs-border-color': 'var(--primaryColor)' }"
                            >
                                <div class="d-flex justify-content-between mb-1">
                                    <strong>{{ comment.user?.label ?? comment.user?.name }}</strong>
                                    <small class="text-muted">{{ formatDate(comment.created_at, 'd.m.Y H:i') }}</small>
                                </div>
                                <div>{{ comment.text }}</div>
                                <div v-if="comment.attachments?.length" class="mt-2 d-flex flex-wrap gap-2">
                                    <router-link
                                        v-for="att in comment.attachments"
                                        :key="att.uuid"
                                        :to="{ name: 'FileDetail', params: { id: att.id } }"
                                        class="d-inline-flex align-items-center gap-1 text-decoration-none small text-body border rounded px-2 py-1"
                                    >
                                        <i class="mdi mdi-paperclip"></i>
                                        {{ att.file_name }}
                                    </router-link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Nový komentář (pouze pokud požadavek není vyřešen a klient není read-only) -->
                <div v-if="!serviceRequirement.is_resolved && !isReadOnly" class="card">
                    <div class="card-header">
                        <div class="card-header__title">{{ t('clientServiceRequirementDetail.add_comment') }}</div>
                    </div>
                    <div class="card-body">
                        <div class="mb-3">
                            <textarea
                                v-model="newCommentText"
                                class="form-control"
                                rows="3"
                                :placeholder="t('clientServiceRequirementDetail.comment_placeholder')"
                            ></textarea>
                        </div>
                        <div class="mb-3">
                            <input
                                type="file"
                                multiple
                                class="form-control"
                                ref="commentFileInput"
                                @change="handleFileChange"
                            />
                        </div>
                        <div class="text-end">
                            <Button
                                :label="isSending ? t('clientServiceRequirementDetail.sending') : t('clientServiceRequirementDetail.send_comment')"
                                icon="mdi-send"
                                :disabled="isSending || !newCommentText.trim()"
                                @click="sendComment"
                            />
                        </div>
                    </div>
                </div>
            </template>
        </div>
    </div>
</template>